import {isFeatureEnabled} from 'feature-flags';
import {Action, Card} from 'platform/components';
import {Box, Center, Show, Spinner} from 'platform/foundation';

import {useState} from 'react';

import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {
  InitializeAiVehicleServiceIntervalsApiResponse,
  useInitializeAiVehicleServiceIntervalsMutation,
} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {
  composePath,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useNavigate,
  buildArray,
} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';
import {VehicleServiceIntervalsAIAttributes} from './components/VehicleServiceIntervalsAIAttributes';
import {VehicleServiceIntervalsAttributes} from './components/VehicleServiceIntervalsAttributes';

interface VehicleServiceIntervalsCardProps extends RequiredTestIdProps {
  vehicleData: GetVehicleApiResponseV2 | Nullish;
}

export function VehicleServiceIntervalsCard(props: VehicleServiceIntervalsCardProps) {
  const navigate = useNavigate();
  const [expectedAiIntervalId, setExpectedAiIntervalId] = useState<string | null>(null);

  const [initializeAiServiceIntervals] = useInitializeAiVehicleServiceIntervalsMutation();

  const navigateToEdit = () => {
    const id = props.vehicleData?.id;
    if (!id) {
      return;
    }
    return navigate(composePath(vehiclesRoutes.editServiceIntervals, {params: {id}}));
  };

  const generateServiceIntervals = () => {
    const vehicleId = props.vehicleData?.id;
    if (!vehicleId) {
      return;
    }

    initializeAiServiceIntervals({vehicleId})
      .unwrap()
      .then((response: InitializeAiVehicleServiceIntervalsApiResponse) => {
        setExpectedAiIntervalId(response.id);
      })
      .catch(handleApiError);
  };

  const isGenerating = expectedAiIntervalId !== null;

  return (
    <Card
      title={i18n.t('entity.vehicle.labels.serviceIntervals')}
      actions={buildArray<Action>()
        .when(isFeatureEnabled(featureFlags.AI_VEHICLE_SERVICE_INTERVALS), {
          type: 'button',
          variant: 'link',
          size: 'small',
          leftIcon: 'action/magic_wand',
          onClick: generateServiceIntervals,
          isDisabled: isGenerating,
          isLoading: isGenerating,
          'data-testid': suffixTestId('generate', props),
          title: i18n.t('entity.vehicle.actions.generateServiceIntervals'),
        })
        .add({
          type: 'button',
          variant: 'link',
          size: 'small',
          leftIcon: 'image/edit',
          onClick: navigateToEdit,
          'data-testid': suffixTestId('edit', props),
          title: i18n.t('general.actions.edit'),
        })}
    >
      <Show when={!isFeatureEnabled(featureFlags.AI_VEHICLE_SERVICE_INTERVALS)}>
        <VehicleServiceIntervalsAttributes
          vehicleData={props.vehicleData}
          data-testid={suffixTestId('serviceIntervals', props)}
        />
      </Show>
      <Show when={isFeatureEnabled(featureFlags.AI_VEHICLE_SERVICE_INTERVALS)}>
        <Box position="relative">
          <VehicleServiceIntervalsAIAttributes
            vehicleData={props.vehicleData}
            expectedAiIntervalId={expectedAiIntervalId}
            onExpectedAiIntervalCompleted={() => setExpectedAiIntervalId(null)}
            data-testid={suffixTestId('serviceIntervals', props)}
          />
          {isGenerating && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              minHeight={8}
              data-testid={suffixTestId('serviceIntervalsLoading', props)}
            >
              <Center>
                <Spinner />
              </Center>
            </Box>
          )}
        </Box>
      </Show>
    </Card>
  );
}
